import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Pill, LogOut, User, Shield, Menu, X } from "lucide-react";
import { useState } from "react";
import NotificationsBell from "@/components/NotificationsBell";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navLink = ({ isActive }) =>
    `px-3 py-2 rounded-lg font-medium text-base transition-colors ${
      isActive
        ? "bg-blue-100 text-blue-900"
        : "text-slate-700 hover:bg-blue-50 hover:text-blue-800"
    }`;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo-link">
          <img
            src="https://customer-assets.emergentagent.com/job_conecta-medicinas/artifacts/padqrbhb_Logo_CE_fondoBlanco.png"
            alt="Ciudad Enfermería"
            className="h-12 w-12 rounded-full object-contain bg-white ring-1 ring-blue-100 shadow-sm"
          />
          <div className="leading-tight">
            <div className="font-display font-bold text-blue-900 text-base sm:text-lg">
              Medicamentos
            </div>
            <div className="text-[11px] sm:text-xs text-slate-500 -mt-0.5 tracking-wide">
              Ciudad Enfermería · SLP
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/medicamentos" className={navLink} data-testid="nav-marketplace">
            Ver disponibles
          </NavLink>
          <NavLink to="/busquedas" className={navLink} data-testid="nav-requests">
            Búsquedas
          </NavLink>
          <NavLink to="/catalogo" className={navLink} data-testid="nav-catalog">
            Catálogo
          </NavLink>
          <NavLink to="/servicios" className={navLink} data-testid="nav-services">
            Servicios
          </NavLink>
          {user && user !== false && (
            <NavLink to="/ofrecer" className={navLink} data-testid="nav-publish">
              Ofrecer
            </NavLink>
          )}
          {user && user !== false && (
            <NavLink to="/mis-publicaciones" className={navLink} data-testid="nav-my-listings">
              Mis publicaciones
            </NavLink>
          )}
          {user && user !== false && (
            <NavLink to="/favoritos" className={navLink} data-testid="nav-favorites">
              Favoritos
            </NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/admin" className={navLink} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1.5"><Shield className="w-4 h-4"/>Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user && user !== false ? (
            <>
              <NotificationsBell />
              <div className="text-sm text-slate-600 max-w-[160px] truncate" data-testid="header-user-name">
                <User className="inline w-4 h-4 mr-1 text-blue-600" />
                {user.name}
              </div>
              <Button variant="outline" onClick={handleLogout} data-testid="header-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-btn">
                Entrar
              </Button>
              <Button onClick={() => navigate("/register")} data-testid="header-register-btn" className="bg-blue-600 hover:bg-blue-700">
                Crear cuenta
              </Button>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-1">
          {user && user !== false && <NotificationsBell />}
          <button
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-blue-50"
            onClick={() => setOpen(!open)}
            aria-label="Menú"
            data-testid="header-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-2">
          <NavLink to="/medicamentos" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-marketplace">Ver disponibles</NavLink>
          <NavLink to="/busquedas" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-requests">Búsquedas</NavLink>
          <NavLink to="/catalogo" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-catalog">Catálogo</NavLink>
          <NavLink to="/servicios" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-services">Servicios de enfermería</NavLink>
          {user && user !== false && (
            <NavLink to="/ofrecer" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-publish">Ofrecer medicamento</NavLink>
          )}
          {user && user !== false && (
            <NavLink to="/mis-publicaciones" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-my-listings">Mis publicaciones</NavLink>
          )}
          {user && user !== false && (
            <NavLink to="/favoritos" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-favorites">Favoritos</NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/admin" onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg hover:bg-blue-50 text-base" data-testid="mobile-nav-admin">Panel Admin</NavLink>
          )}
          <div className="pt-2 border-t border-slate-100 flex gap-2">
            {user && user !== false ? (
              <Button onClick={() => { setOpen(false); handleLogout(); }} variant="outline" className="flex-1" data-testid="mobile-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Salir
              </Button>
            ) : (
              <>
                <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); navigate("/login"); }} data-testid="mobile-login-btn">Entrar</Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => { setOpen(false); navigate("/register"); }} data-testid="mobile-register-btn">Crear cuenta</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
